import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const NewsProofSubmission = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [simStatus, setSimStatus] = useState("");
  const [damageType, setDamageType] = useState("");
  const [otherDamageText, setOtherDamageText] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneSuccess, setPhoneSuccess] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [isCheckingUserData, setIsCheckingUserData] = useState(false);

  const verifiedPhoneNumber = Cookies.get("verifiedPhoneNumber") || "";

  // 필수 입력값 체크하여 버튼 활성화
  useEffect(() => {
    const requiredFieldsFilled = phoneNumber && simStatus && damageType;
    const otherFieldValid =
      damageType !== "기타" || (damageType === "기타" && otherDamageText);
    setIsButtonEnabled(requiredFieldsFilled && otherFieldValid && !phoneError);
  }, [phoneNumber, simStatus, damageType, otherDamageText, phoneError]);

  // 전화번호 검증
  const validatePhoneNumber = (number) => {
    if (number === verifiedPhoneNumber) {
      setPhoneError("");
      setPhoneSuccess(true);
      return true;
    } else {
      setPhoneError("앞에서 인증한 번호와 일치하지 않습니다.");
      setPhoneSuccess(false);
      return false;
    }
  };

  // 전화번호 입력 처리
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 11);
    setPhoneNumber(value);
    if (value.length === 11) {
      validatePhoneNumber(value);
    } else {
      setPhoneError("");
      setPhoneSuccess(false);
    }
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 피해 내용 생성
  const getDamageDescription = () => {
    if (damageType === "개인정보") {
      return "개인정보 유출 우려가 있습니다.";
    } else if (damageType === "보이스피싱") {
      return "개인정보 유출로 인한 보이스피싱 피해를 입었습니다.";
    } else if (damageType === "명의도용") {
      return "개인정보 유출로 인한 명의도용 피해를 입었습니다.";
    } else if (damageType === "기타" && otherDamageText) {
      return otherDamageText;
    }
    return "";
  };

  // 클라이언트 정보 확인 함수
  const checkClientInfo = async () => {
    setIsCheckingUserData(true);
    try {
      // 토큰 가져오기
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);

      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 클라이언트 정보 요청
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("사용자 정보를 가져오는데 실패했습니다.");
      }

      const userData = await response.json();

      // 필수 정보 확인
      const missingFields = [];
      if (!userData.phoneNumber) missingFields.push("휴대폰 번호");
      if (!userData.telecom) missingFields.push("통신사");
      if (!userData.name) missingFields.push("이름");
      if (!userData.email) missingFields.push("이메일");
      if (!userData.address) missingFields.push("주소");
      if (!userData.birthDate) missingFields.push("생년월일");

      if (missingFields.length > 0) {
        setModalTitle("정보 미입력");
        setModalMessage(
          `필수 정보(${missingFields.join(
            ", "
          )})가 누락되었습니다. 기본 정보를 입력해주세요.`
        );
        setShowModal(true);
        return false;
      }

      return true;
    } catch (error) {
      setModalTitle("정보 확인 오류");
      setModalMessage(
        error.message || "사용자 정보를 확인하는 중 오류가 발생했습니다."
      );
      setShowModal(true);
      return false;
    } finally {
      setIsCheckingUserData(false);
    }
  };

  // 애플리케이션 제출 API 호출
  const submitApplication = async () => {
    setIsSubmitting(true);
    setSubmitError("");

    try {
      // 클라이언트 정보 확인
      const isClientInfoValid = await checkClientInfo();
      if (!isClientInfoValid) {
        setIsSubmitting(false);
        return;
      }

      // 토큰 가져오기
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);

      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 스크린샷 파일 키 가져오기
      // const screenshotFileKey = Cookies.get("screenshot_fileKey");

      // if (!screenshotFileKey) {
      //   throw new Error(
      //     "스크린샷 정보가 없습니다. 이전 단계를 다시 진행해주세요."
      //   );
      // }

      // 기본 요청 본문 구성
      const baseRequestBody = {
        lawsuitId: 1,
        isUsimReplaced: simStatus === "완료",
        damageDescription: getDamageDescription(),
        // sktScreenshotFileKey: screenshotFileKey,
        bankCode: Cookies.get("bankCode"),
        accountNumber: Cookies.get("accountNumber"),
        accountHolder: Cookies.get("accountHolder"),
      };

      // 신청 유형 확인
      const applicationType = Cookies.get("applicationType") || "self";
      let response;

      if (applicationType === "representative") {
        // 공동 신청자 정보 가져오기
        const coOwnersJson = Cookies.get("coOwners");
        let coOwners = [];

        if (coOwnersJson) {
          try {
            coOwners = JSON.parse(coOwnersJson);
          } catch (e) {}
        }

        // 대표자 요청 본문 구성
        const withCoApplicantsBody = {
          representative: {
            ...baseRequestBody,
            baseFee: 5000,
            contingencyFeePercentage: 10,
            applicationType: "REPRESENTATIVE",
          },
          coApplicants: coOwners.map((owner) => ({
            name: owner.name,
            phoneNumber: owner.phone,
            birthDate: owner.birth,
            address:
              owner.address +
              (owner.detailAddress ? " " + owner.detailAddress : ""),
            relationship: "공동신청인",
            bankCode: owner.bank ? owner.bank.label : "",
            accountNumber: owner.accountNumber,
            accountHolder: owner.accountHolder,
          })),
        };

        // API 호출 (대표자 신청)
        response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/applications/with-co-applicants`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(withCoApplicantsBody),
          }
        );
      } else {
        // API 호출 (본인 신청)
        response = await fetch(`${API_ENDPOINTS.BASE_URL}/applications`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(baseRequestBody),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();

        // 409 에러 처리 (공동신청자가 이미 결제 완료한 신청서가 있는 경우)
        if (response.status === 409) {
          setModalTitle("이미 결제 완료한 신청서");
          setModalMessage(
            errorData.message ||
              "공동신청자 중 이미 결제 완료한 신청서가 있는 사용자가 있습니다. 공동신청자 목록을 확인해주세요."
          );
          setShowModal(true);
          setIsSubmitting(false);
          return;
        }

        throw new Error(errorData.message || "제출에 실패했습니다.");
      }

      // 응답 데이터 받기
      const responseData = await response.json();

      // applicationType에 따라 다르게 처리
      if (applicationType === "representative") {
        // 대표자 신청의 경우 응답 구조가 다름
        if (
          responseData.application &&
          responseData.application.applicationNumber
        ) {
          // applicationNumber 쿠키에 저장
          Cookies.set(
            "application_number",
            responseData.application.applicationNumber,
            {
              expires: 7, // 7일 유효
              path: "/",
            }
          );

          // 가격 정보를 쿠키에 저장 (응답에서 받아온 값 또는 고정값)
          const baseFee = responseData.application.baseFee || 5000;
          const contingencyFeePercentage =
            responseData.application.contingencyFeePercentage || 10;

          Cookies.set("lawsuit_amount", baseFee, {
            expires: 7,
            path: "/",
          });
          Cookies.set("lawsuit_fee", baseFee, {
            expires: 7,
            path: "/",
          });
          Cookies.set(
            "lawsuit_success_fee_percentage",
            contingencyFeePercentage,
            {
              expires: 7,
              path: "/",
            }
          );

          // 공동 신청자 정보 저장 (옵션)
          if (
            responseData.coApplicants &&
            responseData.coApplicants.length > 0
          ) {
            Cookies.set(
              "coApplicantIds",
              JSON.stringify(responseData.coApplicants.map((ca) => ca.id)),
              {
                expires: 7,
                path: "/",
              }
            );
          }
        } else {
          throw new Error(
            "신청번호를 받지 못했습니다. 나중에 다시 시도해주세요."
          );
        }
      } else {
        // 일반 본인 신청의 경우 기존 로직 유지
        if (responseData.applicationNumber) {
          // applicationNumber 쿠키에 저장
          Cookies.set("application_number", responseData.applicationNumber, {
            expires: 7, // 7일 유효
            path: "/",
          });
        } else {
          throw new Error(
            "신청번호를 받지 못했습니다. 나중에 다시 시도해주세요."
          );
        }

        // 가격 정보를 쿠키에 저장 (고정값 5000원)
        Cookies.set("lawsuit_amount", 5000, {
          expires: 7,
          path: "/",
        });
        Cookies.set("lawsuit_fee", 5000, {
          expires: 7,
          path: "/",
        });
        Cookies.set("lawsuit_success_fee_percentage", 10, {
          expires: 7,
          path: "/",
        });
      }

      // 성공 시 처리
      setSubmitSuccess(true);
      // 소송 금액 선택 페이지를 건너뛰고 바로 계약서 페이지로 이동
      navigate("/agreement");
    } catch (error) {
      setSubmitError(error.message || "제출 중 오류가 발생했습니다.");
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 모달 닫기 및 리다이렉트 처리
  const handleModalClose = () => {
    setShowModal(false);

    // 에러 메시지에 따라 다른 페이지로 리디렉션
    if (
      modalMessage.includes("존재합니다.") ||
      modalMessage.includes("결제 완료한 신청서")
    ) {
      // 409 에러(공동신청자 관련 문제)인 경우 공동 소유자 페이지로 이동
      navigate("/co-owner-data");
    } else {
      // 그 외 사용자 정보 누락 에러의 경우 기본 정보 입력 페이지로 이동
      navigate("/user-data");
    }
  };

  // 다음 페이지로 이동
  const handleNext = () => {
    if (isButtonEnabled && !isSubmitting) {
      submitApplication();
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          {/* 모달 추가 */}
          <AnimatePresence>
            {showModal && (
              <ModalOverlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleModalClose}
              >
                <ModalContent
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ModalTitle>{modalTitle}</ModalTitle>
                  <ModalBody>{modalMessage}</ModalBody>
                  <ModalButton onClick={handleModalClose}>
                    정보 입력하러 가기
                  </ModalButton>
                </ModalContent>
              </ModalOverlay>
            )}
          </AnimatePresence>

          <div>
            <Title>4. 피해정보 확인</Title>
          </div>
          <Header>
            <StepIndicator>4 / 4</StepIndicator>
            <SubTitle>피해 정보 입력</SubTitle>
            <Description>
              피해내용을 입력하시면 빠른 처리를 도와드립니다.
            </Description>
          </Header>

          <FormContainer>
            {submitError && <ErrorAlert>{submitError}</ErrorAlert>}

            <FormGroup>
              <Label>1. 소송 신청 휴대폰 번호를 입력해 주세요</Label>
              <PhoneInputContainer>
                <PhoneInput
                  type="tel"
                  placeholder="-없이 숫자만 입력"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                />
              </PhoneInputContainer>
              {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}
              {phoneSuccess && (
                <SuccessMessage>인증된 번호와 일치합니다.</SuccessMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>2. 유심 교체 여부에 대해 알려주세요</Label>
              <DamageTypeContainer>
                <DamageTypeButton
                  isSelected={simStatus === "완료"}
                  onClick={() => setSimStatus("완료")}
                >
                  유심 교체 완료
                </DamageTypeButton>
                <DamageTypeButton
                  isSelected={simStatus === "미완료"}
                  onClick={() => setSimStatus("미완료")}
                >
                  미완료
                </DamageTypeButton>
              </DamageTypeContainer>
            </FormGroup>

            <FormGroup>
              <Label>3. 피해 주장 내용을 선택해주세요</Label>
              <DamageTypeContainer>
                <DamageTypeButton
                  isSelected={damageType === "개인정보"}
                  onClick={() => setDamageType("개인정보")}
                >
                  개인정보 유출 우려
                </DamageTypeButton>
                <DamageTypeButton
                  isSelected={damageType === "보이스피싱"}
                  onClick={() => setDamageType("보이스피싱")}
                >
                  보이스피싱 피해
                </DamageTypeButton>
                <DamageTypeButton
                  isSelected={damageType === "명의도용"}
                  onClick={() => setDamageType("명의도용")}
                >
                  명의도용 시도/피해
                </DamageTypeButton>
                <DamageTypeButton
                  isSelected={damageType === "기타"}
                  onClick={() => setDamageType("기타")}
                >
                  기타 피해 유형
                </DamageTypeButton>
              </DamageTypeContainer>

              {damageType === "기타" && (
                <OtherDamageInput
                  placeholder="기타 사건 유형을 입력해주세요"
                  value={otherDamageText}
                  onChange={(e) => setOtherDamageText(e.target.value)}
                />
              )}
            </FormGroup>
          </FormContainer>

          <ButtonsContainer>
            <ActionButton
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!isButtonEnabled || isSubmitting}
            >
              {isSubmitting ? "제출 중..." : "다음으로"}
            </ActionButton>
          </ButtonsContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);

    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StepIndicator = styled.div`
  display: flex;
  width: 52px;
  height: 29px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: 5px;
  align-self: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.4;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  width: 100%;
`;

const Label = styled.label`
  display: block;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const PhoneInputContainer = styled.div`
  width: 100%;
  position: relative;
`;

const PhoneInput = styled.input`
  width: 100%;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;

  &::placeholder {
    color: #999;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4c4c;
  font-size: 14px;
  margin-top: 8px;
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  color: #00c853;
  font-size: 14px;
  margin-top: 8px;
  font-weight: 500;
`;

const SimStatusContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
`;

const SimStatusButton = styled.button`
  flex: 1;
  height: 56px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${(props) => (props.isSelected ? "#0075FF" : "#F0F1F3")};
  color: ${(props) => (props.isSelected ? "white" : "#333")};
  transition: all 0.2s ease;
`;

const DamageTypeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
`;

const DamageTypeButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px 10px;
  border-radius: 8px;
  background-color: ${(props) => (props.isSelected ? "#0075FF" : "#EEF1F4")};
  color: ${(props) => (props.isSelected ? "white" : "#333")};
  font-size: 14px;
  font-weight: 500;
  border: 1px solid ${(props) => (props.isSelected ? "#0075FF" : "#EEF1F4")};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  word-break: keep-all;
  min-height: 60px;

  &:hover {
    background-color: ${(props) =>
      props.isSelected ? "#0075FF" : "rgba(0, 117, 255, 0.05)"};
  }
`;

const OtherDamageInput = styled.input`
  width: 100%;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  margin-top: 15px;

  &::placeholder {
    color: #999;
  }
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 30px;
  padding: 0 0px;
  background-color: #fff;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#0075ff")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  @media (min-width: 769px) {
    max-width: 430px;
    margin: 0 auto;
  }
`;

// 추가 스타일
const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 80%;
  max-width: 400px;
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
`;

const ModalBody = styled.p`
  font-size: 16px;
  color: #333;
  margin-bottom: 20px;
`;

const ModalButton = styled.button`
  background-color: #0075ff;
  color: white;
  padding: 10px 20px;
  width: 100%;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

export default NewsProofSubmission;
